import { Injectable, inject } from '@angular/core'
import { NavigationCancel, NavigationError, ResolveEnd, Router } from '@angular/router'

import { createEffect } from '@ngrx/effects'
import { RouterStateSerializer } from '@ngrx/router-store'

import { filter, map } from 'rxjs/operators'

import { StableRouterActions } from '../actions/stable-router.actions'

/**
 * Commits the stable router snapshot on ResolveEnd: after guards and resolvers pass but before
 * route activation, so components constructed during activation already see the new params, while
 * navigations rejected by a guard (e.g. PendingChangesGuard) never reach it.
 */
@Injectable()
export class StableRouterEffects {
  #router = inject(Router)
  #serializer = inject(RouterStateSerializer)

  navigationApproved$ = createEffect(() =>
    this.#router.events.pipe(
      filter((event): event is ResolveEnd => event instanceof ResolveEnd),
      map((event) =>
        StableRouterActions.navigationApproved({
          routerState: this.#serializer.serialize(event.state),
          navigationId: event.id,
        })
      )
    )
  )

  /**
   * Re-aligns the stable snapshot with the route that is actually rendered when a navigation is
   * cancelled or errors after it already reached ResolveEnd. The snapshot commits on ResolveEnd but
   * a superseded/cancelled navigation never activates, so without this it would stay stranded on the
   * aborted target route while the live URL never changed, leaving every stable-router-derived
   * selector reading the wrong params.
   */
  navigationReverted$ = createEffect(() =>
    this.#router.events.pipe(
      filter(
        (event): event is NavigationCancel | NavigationError =>
          event instanceof NavigationCancel || event instanceof NavigationError
      ),
      map((event) =>
        StableRouterActions.navigationApproved({
          routerState: this.#serializer.serialize(this.#router.routerState.snapshot),
          navigationId: event.id,
        })
      )
    )
  )
}
